import { type Collection, isCollection, isSameCollectionType } from './collection';

/**
 * Compare 2 values to see if they are equal (shallower than deepEquals)
 */
export function equals(valueA: any, valueB: any): boolean {
    // biome-ignore lint/suspicious/noSelfCompare: We check for NaN.
    if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
        return true;
    }
    if (!valueA || !valueB) {
        return false;
    }

    if (typeof valueA.valueOf === 'function' && typeof valueB.valueOf === 'function') {
        valueA = valueA.valueOf();
        valueB = valueB.valueOf();
        // biome-ignore lint/suspicious/noSelfCompare: We check for NaN.
        if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
            return true;
        }
        if (!valueA || !valueB) {
            return false;
        }
    }

    if (typeof valueA.equals === 'function') {
        return valueA.equals(valueB);
    }

    return false;
}

/**
 * Deeply compare 2 values to see if they are equal.
 * It can work with any JS objects, including immutable ones.
 */
export function deepEquals(valueA: any, valueB: any): boolean {
    if (equals(valueA, valueB)) {
        return true;
    }

    if (!valueA || !valueB) {
        return false;
    }

    if (isCollection(valueA)) {
        if (!isSameCollectionType(valueA, valueB)) {
            return false;
        }

        if (valueA.size !== valueB.size) {
            return false;
        }

        // @ts-ignore
        if (valueA.constructor.isOrdered) {
            return areOrderedCollectionsEqual(valueA, valueB);
        }

        // @ts-ignore
        if (valueA.constructor.isKeyed) {
            return areKeyedCollectionsEqual(valueA, valueB);
        }

        throw new Error('Unknown collection type');
    }

    if (Array.isArray(valueA) || Array.isArray(valueB)) {
        if (!Array.isArray(valueA) || !Array.isArray(valueB)) {
            return false;
        }
        if (valueA.length !== valueB.length) {
            return false;
        }
        for (let ii = 0; ii < valueA.length; ii++) {
            if (!deepEquals(valueA[ii], valueB[ii])) {
                return false;
            }
        }
        return true;
    }

    if (typeof valueA === 'object' || typeof valueB === 'object') {
        if (typeof valueA !== 'object' || typeof valueB !== 'object') {
            return false;
        }
        const keysA = Object.keys(valueA);
        const keysB = Object.keys(valueB);
        if (keysA.length !== keysB.length) {
            return false;
        }

        for (let ii = 0; ii < keysA.length; ii++) {
            const key = keysA[ii];
            if (key === undefined || !deepEquals(valueA[key], valueB[key])) {
                return false;
            }
        }
        return true;
    }

    return false;
}

function areKeyedCollectionsEqual(colA: Collection<any, any>, colB: Collection<any, any>): boolean {
    const keys = colA.toKeysArray();
    for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        const entryA = colA.getByKey(key);
        const entryB = colB.getByKey(key);

        if (!entryA || !entryB) {
            return false;
        }

        if (!deepEquals(entryA[1], entryB[1])) {
            return false;
        }
    }

    return true;
}

function areOrderedCollectionsEqual(
    colA: Collection<any, any>,
    colB: Collection<any, any>
): boolean {
    for (let index = 0; index < colA.size; index++) {
        const entryA = colA.getByIndex(index);
        const entryB = colB.getByIndex(index);

        if (!entryA || !entryB) {
            return false;
        }

        if (entryA[0] !== entryB[0]) {
            return false;
        }

        if (!deepEquals(entryA[1], entryB[1])) {
            return false;
        }
    }

    return true;
}
